/**
 * /platform/video — відео-інструкції до сторінок (ч.1319).
 *
 * Рішення власника: ролики для YouTube робить ШІ, а працює з ними окрема людина (роль
 * «редактор відео-інструкцій») ЛИШЕ тут, у браузері. Вкладки:
 * - «Довідка» (ч.1370) — уся довідка одним списком з відборами, як «Підтримка → Статті довідки»:
 *   сторінки й документи (ролик до сторінки), теми й питання (ролик за статтею); позначити → «зняти»;
 * - «Сценарії» (ч.1327) — ролик через кілька сторінок: редактор пише завдання словами, агент сам
 *   проходить його в демо-акаунті (справді зберігає й проводить; акаунт після зйомки очищається);
 * - «Памʼять агента» (ч.1363) — правила і вимова слів, які агент виніс із зауважень, для всіх роликів;
 * - «Налаштування» — голос, темп, картинка, оформлення: заставка з назвою, заклик підписатися,
 *   кінцева заставка (ч.1361; для всіх роликів);
 * - «YouTube» — підключення каналу без передачі пароля (вхід Google);
 * - «Запити розробнику» — коли ролик не виходить через саму сторінку.
 * Окремий ролик — /platform/video/:id (перегляд, зауваження агенту, публікація).
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import {
  BUSY_STATUSES,
  fmtDuration,
  platformVideo,
  isPageArticle,
  type DevRequest,
  type HelpArticleKind,
  type VideoGuideListItem,
  type VideoHelpItem,
  type VideoSettings,
  type VideoSettingsOut,
  type VideoStatus,
  type VideoWorkerState,
  DEMO_MODE_HINT, DEMO_MODE_LABEL, type DemoMode,
} from '@/api/platformVideo';
import { extractApiError } from '@/utils/extractApiError';
import { getPlatformStatus } from '@/api/auth';
import { KIND_LABEL } from '@/api/supportHelp';
import VideoBrandingSection, { NumField } from './VideoBrandingSection';
import VideoMemoryTab from './VideoMemoryTab';

type Tab = 'help' | 'topics' | 'memory' | 'settings' | 'youtube' | 'requests';
const TABS: { id: Tab; label: string }[] = [
  { id: 'help', label: 'Довідка' },
  { id: 'topics', label: 'Сценарії' },
  { id: 'memory', label: 'Памʼять агента' },
  { id: 'settings', label: 'Налаштування' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'requests', label: 'Запити розробнику' },
];

/** Повідомлення «зроблено» — зелений бейдж теми (класу .success у проєкті немає). */
export const NOTICE: CSSProperties = {
  background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', border: '1px solid var(--badge-green-bd)',
  borderRadius: 'var(--radius-sm)', padding: '0.5rem 0.75rem', fontSize: '0.875rem', margin: '0.75rem 0 0',
};

const fmtNum = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('uk-UA'));

export const fmtDT = (v: string | null | undefined) => (v ? new Date(v).toLocaleString('uk-UA') : '—');

/** Колір стану ролика (токени теми, Закон дизайну #11). */
function statusTone(s: VideoStatus | null): 'blue' | 'green' | 'amber' | 'red' | null {
  if (!s) return null;
  if (s === 'PUBLISHED' || s === 'APPROVED') return 'green';
  if (s === 'REVIEW') return 'blue';
  if (s === 'FAILED') return 'red';
  if (s === 'OUTDATED' || BUSY_STATUSES.includes(s)) return 'amber';
  return null;
}

export function VideoStatusBadge({ status, label }: { status: VideoStatus | null; label: string }) {
  const tone = statusTone(status);
  if (!tone) return <span style={{ color: 'var(--text-secondary)' }}>{label}</span>;
  return (
    <span data-testid="video-status" style={{
      background: `var(--badge-${tone}-bg)`, color: `var(--badge-${tone}-fg)`,
      border: `1px solid var(--badge-${tone}-bd)`, borderRadius: 4, padding: '0 0.4rem',
      fontSize: '0.8rem', whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

/** ч.1331: довідка, за якою знято ролик, змінилась — редактору перевірити ролик. */
export function HelpChangedBadge({ note }: { note: string | null | undefined }) {
  if (!note) return null;
  return (
    <span data-testid="video-help-changed" title={note} style={{
      marginLeft: '0.35rem', background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
      border: '1px solid var(--badge-amber-bd)', borderRadius: 4, padding: '0 0.35rem', fontSize: '0.75rem',
      whiteSpace: 'nowrap',
    }}>
      довідка змінилась
    </span>
  );
}

/** Робітник не подає сигналу — ролики не знімаються, хоч би що редактор натискав. */
export function WorkerOffline({ worker }: { worker: VideoWorkerState | undefined }) {
  if (!worker || worker.online) return null;
  return (
    <div role="alert" data-testid="video-worker-offline" style={{
      background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)', border: '1px solid var(--badge-amber-bd)',
      borderRadius: 'var(--radius-sm)', padding: '0.5rem 0.75rem', fontSize: '0.875rem', margin: '0 0 0.75rem',
    }}>
      Робітник, що знімає ролики, зараз не працює
      {worker.seen_at ? ` (останній сигнал — ${fmtDT(worker.seen_at)})` : ''}. Завдання стоять у черзі
      й виконаються, коли він запуститься. Якщо це триває довше години — надішліть запит розробнику.
    </div>
  );
}

/** ч.1362: статті довідки в панелі («Підтримка → Статті довідки») відкриває власник платформи
 *  і служба підтримки; редактору відео без цього доступу посилання не показуємо. */
export function useCanOpenHelpArticles(): boolean {
  const st = useQuery({ queryKey: ['platform-status'], queryFn: getPlatformStatus, retry: false, staleTime: 60_000 });
  return !!(st.data?.is_platform_owner || st.data?.is_support_operator);
}

type VideoFilter = '' | 'none' | 'with' | 'review' | 'attention' | 'published' | 'help';
const VIDEO_FILTERS: { id: VideoFilter; label: string; test: (x: VideoHelpItem) => boolean }[] = [
  { id: '', label: 'усі', test: () => true },
  { id: 'none', label: 'без ролика', test: (x) => !x.guide_id },
  { id: 'with', label: 'з роликом', test: (x) => !!x.guide_id },
  { id: 'review', label: 'чекають перевірки', test: (x) => x.status === 'REVIEW' },
  { id: 'attention', label: 'потребують уваги', test: (x) => x.status === 'OUTDATED' || x.status === 'FAILED' },
  { id: 'help', label: 'довідка змінилась', test: (x) => !!x.help_change_note },
  { id: 'published', label: 'опубліковані', test: (x) => x.status === 'PUBLISHED' },
];

/** ч.1370: де стаття живе — сторінка для статей сторінок, сторінка питання для питань. */
function placeOf(x: VideoHelpItem): string | null {
  if (isPageArticle(x.kind)) return x.route_pattern;
  return x.parent_route && !x.parent_route.startsWith('§') ? x.parent_route : null;
}

/** ч.1370 (рішення власника): уся довідка одним списком з відборами, як «Підтримка → Статті
 *  довідки», — сторінки, документи, теми й питання разом. Ролик до сторінки знімається за статтею
 *  сторінки, ролик за статтею — для теми чи питання. Сценарії — окремою вкладкою. */
function HelpTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [needle, setNeedle] = useState('');
  const [section, setSection] = useState('');
  const [kind, setKind] = useState<'' | HelpArticleKind>('');
  const [video, setVideo] = useState<VideoFilter>('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // ч.1362: завдання агенту від редактора — для роликів до сторінок, поставлених цією кнопкою.
  const [brief, setBrief] = useState('');
  const [demoMode, setDemoMode] = useState<DemoMode>('OWNER');
  const canOpenArticle = useCanOpenHelpArticles();
  const list = useQuery({
    queryKey: ['platform-video-help'],
    queryFn: platformVideo.help,
    // поки агент працює — стан оновлюється сам
    refetchInterval: (query) => (query.state.data?.items.some((x) => x.status && BUSY_STATUSES.includes(x.status))
      ? 10_000 : false),
  });
  const all = useMemo(() => list.data?.items ?? [], [list.data]);
  const orphans = list.data?.orphans ?? [];
  const sections = useMemo(() => Array.from(new Set(all.map((x) => x.section).filter((s): s is string => !!s))), [all]);
  const rows = useMemo(() => {
    const test = VIDEO_FILTERS.find((f) => f.id === video)!.test;
    const q = needle.trim().toLowerCase();
    return all.filter((x) => test(x) && (!section || x.section === section) && (!kind || x.kind === kind)
      && (!q || `${x.title} ${placeOf(x) ?? ''} ${x.section ?? ''}`.toLowerCase().includes(q)));
  }, [all, video, section, kind, needle]);
  // «Зняти для всіх без ролика» — за поточним відбором (розділ, вид, пошук).
  const withoutVideo = rows.filter((x) => !x.guide_id);

  const create = useMutation({
    mutationFn: async (slugs: string[]) => {
      const chosen = all.filter((x) => slugs.includes(x.slug) && !x.guide_id);
      const routes = chosen.filter((x) => isPageArticle(x.kind) && x.route_pattern).map((x) => x.route_pattern!);
      const articles = chosen.filter((x) => !isPageArticle(x.kind)).map((x) => x.slug);
      let created = 0;
      if (routes.length) created += (await platformVideo.create(routes, brief, demoMode)).created;
      if (articles.length) created += (await platformVideo.createFaq(articles, demoMode)).created;
      return created;
    },
    onSuccess: (created) => {
      setErr(null);
      setMsg(`Поставлено в чергу: ${created}. Агент напише сценарій і зніме ролик — коли буде готово, прийде лист.`);
      setPicked(new Set());
      setBrief('');
      setDemoMode('OWNER');
      qc.invalidateQueries({ queryKey: ['platform-video-help'] });
    },
    onError: (e) => { setMsg(null); setErr(extractApiError(e)); },
  });

  const toggle = (slug: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    return next;
  });

  const columns: ColumnDef<VideoHelpItem>[] = [
    {
      key: 'pick', header: '', width: 40, align: 'center',
      cell: (x) => (x.guide_id ? null : (
        <input type="checkbox" aria-label={`Позначити: ${x.title}`} checked={picked.has(x.slug)}
          onChange={() => toggle(x.slug)} style={{ width: 'auto', margin: 0 }} />
      )),
    },
    {
      key: 'title', header: 'Назва', width: 320, sortValue: (x) => x.title,
      cell: (x) => (x.guide_id ? <Link to={`/platform/video/${x.guide_id}`}>{x.title}</Link> : x.title),
    },
    {
      key: 'status', header: 'Ролик', width: 230, sortValue: (x) => x.status ?? '',
      cell: (x) => <><VideoStatusBadge status={x.status} label={x.status_label} /><HelpChangedBadge note={x.help_change_note} /></>,
    },
    { key: 'kind', header: 'Вид', width: 130, sortValue: (x) => KIND_LABEL[x.kind], cell: (x) => KIND_LABEL[x.kind] },
    { key: 'section', header: 'Розділ', width: 150, sortValue: (x) => x.section ?? '', cell: (x) => x.section ?? '—' },
    {
      key: 'place', header: 'Стаття', width: 220, sortValue: (x) => placeOf(x) ?? '',
      cell: (x) => {
        const label = placeOf(x) ? `/${placeOf(x)}` : 'відкрити';
        return canOpenArticle
          ? <Link to={`/platform/support-help/${x.article_id}`} title="Відкрити статтю довідки">{label}</Link>
          : (placeOf(x) ? label : '—');
      },
    },
    {
      key: 'remarks', header: 'Зауваження', width: 110, align: 'center',
      sortValue: (x) => Number(x.open_remarks ?? 0),
      cell: (x) => (Number(x.open_remarks) > 0
        ? <span title="Зауваження, які агент ще опрацьовує">{x.open_remarks}</span> : ''),
    },
    { key: 'duration', header: 'Тривалість', width: 100, align: 'right', cell: (x) => fmtDuration(x.duration_sec) },
    {
      key: 'youtube', header: 'YouTube', width: 100, align: 'center',
      cell: (x) => (x.youtube_url ? <a href={x.youtube_url} target="_blank" rel="noreferrer">дивитись</a> : ''),
    },
    { key: 'updated', header: 'Оновлено', width: 160, sortValue: (x) => x.updated_at ?? '', cell: (x) => fmtDT(x.updated_at) },
  ];
  const pickedPages = all.some((x) => picked.has(x.slug) && isPageArticle(x.kind));

  return (
    <>
      <WorkerOffline worker={list.data?.worker} />
      <div className="doc-grid" style={{ marginBottom: '0.6rem' }}>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label htmlFor="vh-q">Пошук</label>
          <input id="vh-q" type="search" value={needle} onChange={(e) => setNeedle(e.target.value)}
            placeholder="слово з назви чи адреса сторінки" />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label htmlFor="vh-section">Розділ</label>
          <select id="vh-section" value={section} onChange={(e) => setSection(e.target.value)}>
            <option value="">усі розділи</option>
            {sections.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label htmlFor="vh-kind">Вид</label>
          <select id="vh-kind" value={kind} onChange={(e) => setKind(e.target.value as '' | HelpArticleKind)}>
            <option value="">усі</option>
            {(Object.keys(KIND_LABEL) as HelpArticleKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label htmlFor="vh-video">Ролик</label>
          <select id="vh-video" value={video} onChange={(e) => setVideo(e.target.value as VideoFilter)}>
            {VIDEO_FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </div>
      </div>
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          {list.isLoading ? 'завантаження…' : `${rows.length} з ${all.length}`}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" className="primary" disabled={!picked.size || create.isPending}
          title={picked.size ? undefined : 'Позначте статті без ролика галками ліворуч'}
          onClick={() => create.mutate(Array.from(picked))}>
          зняти ролики для позначених{picked.size ? ` (${picked.size})` : ''}
        </button>
        <button type="button" disabled={!withoutVideo.length || create.isPending}
          title={withoutVideo.length ? undefined : 'За цим відбором ролики вже є для всіх статей'}
          onClick={() => {
            if (window.confirm(`Зняти ролики для всіх статей без ролика за поточним відбором (${withoutVideo.length})? `
              + 'Агент робитиме їх по черзі, це може тривати кілька годин.')) {
              create.mutate(withoutVideo.map((x) => x.slug));
            }
          }}>
          зняти для всіх без ролика ({withoutVideo.length})
        </button>
      </div>
      <DemoModeField id="video-pages-mode" value={demoMode} onChange={setDemoMode} />
      <div className="field" style={{ margin: '0.25rem 0 0.75rem' }}>
        <label htmlFor="video-pages-brief">Завдання агенту для позначених сторінок (необовʼязково)</label>
        <textarea id="video-pages-brief" rows={2} maxLength={4000} value={brief} disabled={!pickedPages && !!picked.size}
          placeholder="Наприклад: покажи обидві вкладки рядків, заповни і проведи документ, наприкінці — друк."
          onChange={(e) => setBrief(e.target.value)} />
        <div className="cell-hint">
          Для ролика до сторінки агент проходить статтю сторінки розділ за розділом; завдання стає першим
          правилом (змінити — на сторінці ролика). Ролик за темою чи питанням знімається за самою статтею.
        </div>
      </div>
      {msg && <div role="status" style={NOTICE}>{msg}</div>}
      {err && <div className="error">{err}</div>}
      {orphans.length > 0 && (
        <div data-testid="video-faq-orphans" style={{ margin: '0.5rem 0 0.75rem', padding: '0.5rem 0.75rem',
          background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', borderRadius: 'var(--radius-sm)',
          color: 'var(--badge-amber-fg)', fontSize: '0.875rem' }}>
          <b>Ролики, чиєї статті в довідці вже немає</b> (питання чи тему перейменували або прибрали). Відкрийте
          ролик — його можна перепривʼязати до іншої статті або видалити:
          <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.2rem' }}>
            {orphans.map((o) => (
              <li key={o.guide_id}><Link to={`/platform/video/${o.guide_id}`}>{o.title}</Link></li>
            ))}
          </ul>
        </div>
      )}
      <DataTable rows={rows} columns={columns} rowKey={(x) => x.slug} loading={list.isLoading}
        emptyText="Статей за цим відбором немає" storageKey="platform-video-help"
        onRowDoubleClick={(x) => { if (x.guide_id) navigate(`/platform/video/${x.guide_id}`); }} />
    </>
  );
}

/* ───────────────────────────── Сценарії (ч.1327) ───────────────────────────── */

const BRIEF_EXAMPLE = 'Наприклад: покажи, як виставити рахунок покупцю на товари, провести його, на його підставі '
  + 'створити реалізацію і провести її, а наприкінці — де видно оплату від покупця.';

function TopicsTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [demoMode, setDemoMode] = useState<DemoMode>('OWNER');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['platform-video-topics'],
    queryFn: () => platformVideo.guides('TOPIC'),
    refetchInterval: (query) => (query.state.data?.items.some((g) => BUSY_STATUSES.includes(g.status))
      ? 10_000 : false),
  });
  const create = useMutation({
    mutationFn: () => platformVideo.createTopic(title.trim(), brief.trim(), demoMode),
    onSuccess: () => {
      setErr(null); setTitle(''); setBrief(''); setDemoMode('OWNER');
      setMsg('Поставлено в чергу. Агент пройде сценарій у демо-акаунті, озвучить і зніме ролик — '
        + 'коли буде готово, прийде лист.');
      qc.invalidateQueries({ queryKey: ['platform-video-topics'] });
    },
    onError: (e) => { setMsg(null); setErr(extractApiError(e)); },
  });
  const blocked = title.trim().length < 3 ? 'Вкажіть назву ролика'
    : brief.trim().length < 10 ? 'Опишіть, що показати в ролику' : undefined;
  const columns: ColumnDef<VideoGuideListItem>[] = [
    {
      key: 'title', header: 'Сценарій', sortValue: (g) => g.title,
      cell: (g) => (
        <span title={g.brief ?? undefined}>
          <Link to={`/platform/video/${g.guide_id}`}>{g.title}</Link>
          {g.brief ? <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}> · {g.brief}</span> : null}
        </span>
      ),
    },
    {
      key: 'pages', header: 'Сторінки', width: 90, align: 'center',
      sortValue: (g) => g.visited_routes.length,
      cell: (g) => (g.visited_routes.length
        ? <span title={g.visited_routes.map((r) => `/${r}`).join('\n')}>{g.visited_routes.length}</span> : ''),
    },
    {
      key: 'status', header: 'Ролик', width: 230, sortValue: (g) => g.status,
      cell: (g) => <><VideoStatusBadge status={g.status} label={g.status_label} /><HelpChangedBadge note={g.help_change_note} /></>,
    },
    { key: 'duration', header: 'Тривалість', width: 100, align: 'right', cell: (g) => fmtDuration(g.duration_sec) },
    {
      key: 'youtube', header: 'YouTube', width: 100, align: 'center',
      cell: (g) => (g.youtube_url ? <a href={g.youtube_url} target="_blank" rel="noreferrer">дивитись</a> : ''),
    },
    { key: 'updated', header: 'Оновлено', width: 160, sortValue: (g) => g.updated_at ?? '', cell: (g) => fmtDT(g.updated_at) },
  ];
  return (
    <>
      <WorkerOffline worker={list.data?.worker} />
      <form onSubmit={(e) => { e.preventDefault(); if (!blocked) create.mutate(); }}>
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label htmlFor="vt-title">Назва ролика</label>
            <input id="vt-title" value={title} maxLength={200} placeholder="Від рахунку до оплати"
              onChange={(e) => { setTitle(e.target.value); setMsg(null); }} />
          </div>
          <div className="field" style={{ gridColumn: 'span 8' }}>
            <label htmlFor="vt-brief">Що показати (звичайними словами)</label>
            <textarea id="vt-brief" rows={3} value={brief} maxLength={4000} placeholder={BRIEF_EXAMPLE}
              onChange={(e) => { setBrief(e.target.value); setMsg(null); }} />
          </div>
        </div>
        <DemoModeField id="vt-mode" value={demoMode} onChange={setDemoMode} />
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="submit" className="primary" disabled={!!blocked || create.isPending} title={blocked}>
            {create.isPending ? 'ставимо в чергу…' : 'зняти ролик'}
          </button>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Агент сам обере сторінки, відкриє вкладки й документи, справді збереже і проведе документи в
            демо-акаунті — після зйомки демо-акаунт очищається.
          </span>
        </div>
      </form>
      {msg && <div role="status" style={NOTICE}>{msg}</div>}
      {err && <div className="error">{err}</div>}
      <h3>Ролики-сценарії</h3>
      <DataTable rows={list.data?.items ?? []} columns={columns} rowKey={(g) => g.guide_id} loading={list.isLoading}
        emptyText="Сценаріїв ще немає — опишіть перший вище" storageKey="platform-video-topics"
        onRowDoubleClick={(g) => navigate(`/platform/video/${g.guide_id}`)} />
    </>
  );
}

/* ───────────────────────────── Налаштування ───────────────────────────── */

const PROVIDER_LABEL: Record<VideoSettings['voice_provider'], string> = {
  piper: 'Piper — безкоштовний, на нашому сервері',
  elevenlabs: 'ElevenLabs — найприродніший, платний',
  azure: 'Azure — природний, платний',
};

/** ч.1413: у якому режимі робот зніматиме ролик. */
export function DemoModeField({ id, value, onChange, disabled }: {
  id: string; value: DemoMode; onChange: (m: DemoMode) => void; disabled?: boolean;
}) {
  return (
    <div className="field" style={{ maxWidth: '32rem' }}>
      <label htmlFor={id}>Режим демо-акаунта</label>
      <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as DemoMode)}>
        {(Object.keys(DEMO_MODE_LABEL) as DemoMode[]).map((m) => (
          <option key={m} value={m}>{DEMO_MODE_LABEL[m]}</option>
        ))}
      </select>
      <div className="cell-hint">{DEMO_MODE_HINT[value]}</div>
    </div>
  );
}

/** ч.1331: демо-акаунт робота — що в ньому, коли очищено, «очистити й наповнити зараз». */
function DemoBlock({ demo }: { demo: NonNullable<VideoSettingsOut['demo']> }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const reset = useMutation({
    mutationFn: platformVideo.demoReset,
    onSuccess: () => {
      setMsg('Поставлено: робітник очистить і наповнить демо-акаунт протягом хвилини (якщо зараз не знімає ролик).');
      qc.invalidateQueries({ queryKey: ['platform-video-settings'] });
    },
  });
  return (
    <section data-testid="video-demo-block" style={{ marginBottom: '1rem' }}>
      <h3 style={{ marginTop: 0 }}>Демо-акаунт робота</h3>
      <p data-testid="video-demo-state" style={{ margin: '0 0 0.35rem', fontSize: '0.875rem',
        color: demo.demo_reset_error ? 'var(--danger)' : 'var(--text-secondary)' }}>
        {demo.demo_reset_error
          ? `Останнє очищення не вдалося — ${demo.demo_reset_error}`
          : demo.demo_reset_at
            ? `Очищено й наповнено ${fmtDT(demo.demo_reset_at)}`
            : 'Ще не очищався'}
        {demo.demo_dirty ? ' · зараз у ньому працює робот' : ''}
        {demo.demo_reset_requested_at ? ' · очищення поставлено в чергу' : ''}
      </p>
      <p style={{ margin: '0 0 0.35rem', fontSize: '0.875rem' }}>
        Робот входить в окремий акаунт з вигаданими даними{demo.email ? ` (${demo.email})` : ''} і справді
        зберігає та проводить у ньому документи. Перед кожною зйомкою і після неї акаунт повністю
        видаляється і створюється наново з тим самим наповненням — тому кожен ролик починається з
        однакових даних. Зміни, зроблені в демо-акаунті вручну, теж зникнуть при наступному очищенні: щоб
        дані були в демо-акаунті завжди, їх додає розробник у наповнення (запит розробнику).
      </p>
      {demo.content && demo.content.length > 0 && (
        <ul style={{ margin: '0 0 0.5rem', paddingLeft: '1.2rem', fontSize: '0.875rem' }}>
          {demo.content.map((line) => <li key={line}>{line}</li>)}
        </ul>
      )}
      <button type="button" disabled={reset.isPending || !!demo.demo_reset_requested_at}
        title="Видалити все в демо-акаунті і наповнити наново (наприклад, після ручних змін)"
        onClick={() => reset.mutate()}>
        очистити й наповнити зараз
      </button>
      {msg && <div role="status" style={NOTICE}>{msg}</div>}
      {reset.error && <div className="error">{extractApiError(reset.error)}</div>}
    </section>
  );
}

function SettingsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform-video-settings'], queryFn: platformVideo.settings });
  const [form, setForm] = useState<VideoSettings | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (q.data && !form) setForm(q.data.settings); }, [q.data, form]);
  const save = useMutation({
    mutationFn: (s: VideoSettings) => platformVideo.saveSettings(s),
    onSuccess: (r) => {
      setErr(null); setForm(r.settings);
      setMsg('Збережено. Нові налаштування діють на наступні зйомки; щоб перезняти готовий ролик — '
        + 'кнопка «перезняти» на його сторінці.');
      qc.invalidateQueries({ queryKey: ['platform-video-settings'] });
    },
    onError: (e) => { setMsg(null); setErr(extractApiError(e)); },
  });
  const listen = useMutation({
    mutationFn: async () => {
      const url = await platformVideo.sample(form!.voice, form!.elevenlabs_model, form!.speech_rate);
      const audio = new Audio(url);
      audio.addEventListener('ended', () => URL.revokeObjectURL(url));
      try {
        await Promise.resolve(audio.play());
      } catch { /* браузер заборонив автовідтворення — звук не критичний */ }
    },
  });
  if (q.isLoading || !form) return <p>Завантаження…</p>;
  const set = <K extends keyof VideoSettings>(k: K, v: VideoSettings[K]) => {
    setMsg(null); setForm((f) => (f ? { ...f, [k]: v } : f));
  };
  const providers = q.data?.providers ?? {};
  const voices = q.data?.voices?.[form.voice_provider] ?? [];
  const eleven = q.data?.elevenlabs;
  const isEleven = form.voice_provider === 'elevenlabs';
  const quota = eleven?.quota;

  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }}>
      <WorkerOffline worker={q.data?.worker} />
      {q.data?.demo && <DemoBlock demo={q.data.demo} />}
      <h3 style={{ marginTop: 0 }}>Голос</h3>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label htmlFor="vs-provider">Синтезатор мови</label>
          <select id="vs-provider" value={form.voice_provider}
            onChange={(e) => {
              const p = e.target.value as VideoSettings['voice_provider'];
              set('voice_provider', p);
              const list = q.data?.voices?.[p] ?? [];
              const preferred = p === 'elevenlabs' ? eleven?.default_voice : undefined;
              set('voice', list.find((v) => v.id === preferred)?.id ?? list[0]?.id ?? '');
            }}>
            {(Object.keys(PROVIDER_LABEL) as VideoSettings['voice_provider'][]).map((p) => (
              <option key={p} value={p} disabled={!providers[p] && p !== form.voice_provider}>
                {PROVIDER_LABEL[p]}{providers[p] ? '' : (q.data?.worker.online ? ' — не підключено' : '')}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: isEleven ? 'span 4' : 'span 8' }}>
          <label htmlFor="vs-voice">Голос</label>
          {voices.length ? (
            <select id="vs-voice" value={form.voice} onChange={(e) => set('voice', e.target.value)}>
              {!voices.some((v) => v.id === form.voice) && form.voice && (
                <option value={form.voice}>{form.voice}</option>
              )}
              {voices.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          ) : (
            <input id="vs-voice" value={form.voice} placeholder="ідентифікатор голосу з кабінету ElevenLabs"
              onChange={(e) => set('voice', e.target.value)} />
          )}
        </div>
        {isEleven && (
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label htmlFor="vs-model">Модель ElevenLabs</label>
            <select id="vs-model" value={form.elevenlabs_model}
              onChange={(e) => set('elevenlabs_model', e.target.value)}>
              {!eleven?.models.some((m) => m.id === form.elevenlabs_model) && (
                <option value={form.elevenlabs_model}>{form.elevenlabs_model}</option>
              )}
              {(eleven?.models ?? []).map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
        )}
      </div>
      {isEleven && eleven?.error && <div className="error">{eleven.error}</div>}
      <div className="doc-grid">
        <NumField id="vs-rate" label="Швидкість мовлення" value={form.speech_rate} min={0.6} max={1.6} step={0.05}
          onChange={(v) => set('speech_rate', v)} hint="1 — звичайна; 0,9 — повільніше; 1,1 — швидше" />
        <NumField id="vs-pause" label="Пауза після фрази, мс" value={form.pause_after_ms} min={0} max={3000} step={100}
          onChange={(v) => set('pause_after_ms', v)} />
        <div className="field" style={{ gridColumn: 'span 6', justifyContent: 'flex-end' }}>
          {isEleven ? (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => listen.mutate()} disabled={listen.isPending || !form.voice}
                title="Коротка фраза українською цим голосом, моделлю і темпом (повторне прослуховування символів не витрачає)">
                {listen.isPending ? 'озвучуємо…' : 'прослухати'}
              </button>
              {quota && (
                <span data-testid="eleven-quota" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  ElevenLabs: використано {fmtNum(quota.used)} з {fmtNum(quota.limit)} символів
                  {quota.reset_unix ? ` до ${new Date(quota.reset_unix * 1000).toLocaleDateString('uk-UA')}` : ''}
                </span>
              )}
            </div>
          ) : (
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Голос Piper звучить так само, як у вже знятих роликах.
            </span>
          )}
        </div>
      </div>
      {listen.error && <div className="error">{extractApiError(listen.error)}</div>}

      <h3>Картинка</h3>
      <div className="doc-grid">
        <NumField id="vs-w" label="Ширина вікна, px" value={form.viewport_width} min={1024} max={1920} step={2}
          onChange={(v) => set('viewport_width', v)} />
        <NumField id="vs-h" label="Висота вікна, px" value={form.viewport_height} min={576} max={1080} step={2}
          onChange={(v) => set('viewport_height', v)} />
        <NumField id="vs-zoom" label="Збільшення" value={form.zoom} min={1} max={2} step="any"
          onChange={(v) => set('zoom', v)} hint="1,33 — ролик 1920×1080 з вікна 1440×810: текст крупніший" />
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label htmlFor="vs-theme">Тема сторінки</label>
          <select id="vs-theme" value={form.theme} onChange={(e) => set('theme', e.target.value as VideoSettings['theme'])}>
            <option value="light">денна</option>
            <option value="dark">нічна</option>
          </select>
        </div>
      </div>
      <div className="doc-grid">
        <NumField id="vs-cursor" label="Розмір курсора, px" value={form.cursor_size} min={12} max={64} step={2}
          onChange={(v) => set('cursor_size', v)} />
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label htmlFor="vs-hl">Колір підсвітки</label>
          <input id="vs-hl" type="color" value={form.highlight_color}
            onChange={(e) => set('highlight_color', e.target.value)} style={{ height: '2.2rem', padding: 2 }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }} />
      </div>

      {/* ч.1361: оформлення ролика — заставка з назвою, заклик підписатися, кінцева заставка */}
      <VideoBrandingSection form={form} set={set} branding={q.data?.branding} canListen={isEleven && !!form.voice} />

      <h3>Субтитри</h3>
      <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
        <input type="checkbox" checked={form.burn_subtitles} style={{ width: 'auto', margin: 0 }}
          onChange={(e) => set('burn_subtitles', e.target.checked)} />
        вшити субтитри в картинку (на YouTube субтитри й так додаються окремим файлом)
      </label>

      <h3>Автоматизація і публікація</h3>
      <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
        <input type="checkbox" checked={form.auto_rerender_outdated} style={{ width: 'auto', margin: 0 }}
          onChange={(e) => set('auto_rerender_outdated', e.target.checked)} />
        коли сторінка змінилась і ролик став неточним — агент сам поправить сценарій і перезніме
        (інакше ролик лише позначиться «сторінка змінилась»)
      </label>
      <div className="doc-grid" style={{ marginTop: '0.5rem' }}>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label htmlFor="vs-privacy">Видимість на YouTube</label>
          <select id="vs-privacy" value={form.youtube_privacy}
            onChange={(e) => set('youtube_privacy', e.target.value as VideoSettings['youtube_privacy'])}>
            <option value="unlisted">за посиланням (не в пошуку)</option>
            <option value="public">для всіх</option>
            <option value="private">приватний</option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 8' }} />
      </div>

      {msg && <div role="status" style={NOTICE}>{msg}</div>}
      {err && <div className="error">{err}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button type="submit" className="primary" disabled={save.isPending}>
          {save.isPending ? 'зберігаємо…' : 'зберегти'}
        </button>
        <button type="button" onClick={() => { setForm(q.data!.settings); setMsg(null); }}>скасувати зміни</button>
      </div>
    </form>
  );
}

/* ───────────────────────────── YouTube ───────────────────────────── */

const RETURN_TEXT: Record<string, { ok: boolean; text: string }> = {
  connected: { ok: true, text: 'Канал підключено. Схвалені ролики тепер публікуються на YouTube.' },
  denied: { ok: false, text: 'Підключення скасовано у Google — канал не підключено.' },
  failed: { ok: false, text: 'Google не підтвердив підключення. Спробуйте ще раз.' },
};

function YoutubeTab({ returned }: { returned: string | null }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform-video-youtube'], queryFn: platformVideo.youtube });
  const [err, setErr] = useState<string | null>(null);
  const connect = useMutation({
    mutationFn: platformVideo.youtubeConnect,
    onSuccess: (r) => { window.location.assign(r.url); },
    onError: (e) => setErr(extractApiError(e)),
  });
  const disconnect = useMutation({
    mutationFn: platformVideo.youtubeDisconnect,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-video-youtube'] }),
    onError: (e) => setErr(extractApiError(e)),
  });
  const ret = returned ? RETURN_TEXT[returned] : undefined;
  if (q.isLoading) return <p>Завантаження…</p>;
  const st = q.data;
  return (
    <div style={{ maxWidth: '44rem' }}>
      {ret && (ret.ok
        ? <div role="status" style={NOTICE}>{ret.text}</div>
        : <div className="error" role="status">{ret.text}</div>)}
      {!st?.configured ? (
        <p>
          Підключення YouTube ще не налаштоване на сервері. Надішліть запит розробнику (вкладка
          «Запити розробнику») — після налаштування тут зʼявиться кнопка «підключити канал».
          До того ролики можна схвалювати і завантажувати файлом.
        </p>
      ) : st.connected ? (
        <>
          <p>
            Підключено канал <b>{st.channel_title ?? st.channel_id}</b>
            {st.connected_at ? ` (з ${fmtDT(st.connected_at)})` : ''}. Схвалений ролик публікується
            на цей канал автоматично, з назвою, описом і субтитрами.
          </p>
          <button type="button" disabled={disconnect.isPending}
            onClick={() => { if (window.confirm('Відключити канал? Опубліковані ролики лишаться на YouTube.')) disconnect.mutate(); }}>
            відключити канал
          </button>
        </>
      ) : (
        <>
          <p>
            Натисніть «підключити канал» — відкриється вхід Google. Увійдіть обліковим записом, якому
            належить канал, і дозвольте завантаження відео. Пароль від каналу сюди не передається і ніде
            не зберігається: сервіс отримує лише дозвіл завантажувати ролики, який можна відкликати в
            налаштуваннях облікового запису Google.
          </p>
          <button type="button" className="primary" disabled={connect.isPending} onClick={() => connect.mutate()}>
            підключити канал
          </button>
        </>
      )}
      {err && <div className="error">{err}</div>}
    </div>
  );
}

/* ───────────────────────────── Запити розробнику ───────────────────────────── */

export function DevRequestForm({ guideId, onSent }: { guideId?: string | null; onSent?: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => platformVideo.addRequest(text.trim(), guideId),
    onSuccess: () => {
      setText(''); setErr(null); setMsg('Надіслано розробнику.');
      qc.invalidateQueries({ queryKey: ['platform-video-requests'] });
      onSent?.();
    },
    onError: (e) => { setMsg(null); setErr(extractApiError(e)); },
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); send.mutate(); }}>
      <div className="field">
        <label htmlFor="vd-req">Що не так зі сторінкою або з роботою агента</label>
        <textarea id="vd-req" rows={4} value={text} onChange={(e) => { setText(e.target.value); setMsg(null); }}
          placeholder="Наприклад: на сторінці «Банк: списання» кнопка «провести» закрита вікном — агент не може її показати" />
      </div>
      {msg && <div role="status" style={NOTICE}>{msg}</div>}
      {err && <div className="error">{err}</div>}
      <button type="submit" className="primary" disabled={text.trim().length < 3 || send.isPending}>
        {send.isPending ? 'надсилаємо…' : 'надіслати розробнику'}
      </button>
    </form>
  );
}

function RequestsTab() {
  const q = useQuery({ queryKey: ['platform-video-requests'], queryFn: platformVideo.requests });
  const columns: ColumnDef<DevRequest>[] = [
    { key: 'at', header: 'Коли', width: 160, sortValue: (r) => r.created_at, cell: (r) => fmtDT(r.created_at) },
    { key: 'who', header: 'Хто', width: 200, cell: (r) => r.author_name || r.author_email || '—' },
    { key: 'guide', header: 'Ролик', width: 220, cell: (r) => r.guide_title ?? '—' },
    { key: 'text', header: 'Запит', cell: (r) => <span style={{ whiteSpace: 'pre-wrap' }}>{r.text}</span> },
    { key: 'status', header: 'Стан', width: 110, cell: (r) => (r.status === 'DONE' ? 'виконано' : 'новий') },
  ];
  return (
    <>
      <p style={{ marginTop: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Зауваження до самого ролика (темп, що показати, текст) пишіть на сторінці ролика — їх виконує
        агент. Сюди — те, що агент змінити не може: помилка на сторінці, незрозуміла кнопка, потрібна
        нова можливість.
      </p>
      <DevRequestForm />
      <h3>Надіслані запити</h3>
      <DataTable rows={q.data ?? []} columns={columns} rowKey={(r) => r.id} loading={q.isLoading}
        emptyText="Запитів ще не було" />
    </>
  );
}

export default function PlatformVideoPage() {
  const [params, setParams] = useSearchParams();
  const youtubeReturn = params.get('youtube');
  // ч.1370: колишні вкладки «Сторінки» і «Часті питання» — тепер одна «Довідка» (старі посилання ведуть туди).
  const raw = params.get('tab');
  const tab: Tab = raw && TABS.some((t) => t.id === raw) ? raw as Tab : youtubeReturn ? 'youtube' : 'help';
  const setTab = (t: Tab) => setParams(t === 'help' ? {} : { tab: t }, { replace: true });
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Відео-інструкції</h2>
      </div>
      <p style={{ marginTop: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Ролики робить агент: до сторінки — за статтею довідки, за темою чи питанням довідки — за самою
        статтею, сценарій — за вашим завданням через кілька сторінок. Агент проходить ролик у демо-акаунті з вигаданими
        даними (справді зберігає й проводить; після зйомки демо-акаунт очищається), озвучує і записує екран.
        Ви дивитесь, пишете зауваження і публікуєте.
      </p>
      <div className="seg-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" className={tab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ marginTop: '0.75rem' }}>
        {tab === 'help' && <HelpTab />}
        {tab === 'topics' && <TopicsTab />}
        {tab === 'memory' && <VideoMemoryTab />}
        {tab === 'settings' && <SettingsTab />}
        {tab === 'youtube' && <YoutubeTab returned={youtubeReturn} />}
        {tab === 'requests' && <RequestsTab />}
      </div>
    </div>
  );
}
